import { DialogProps } from '@radix-ui/react-dialog'
import { Command as CommandPrimitive } from 'cmdk'
import { Dialog, DialogContent } from 'components/ui/dialog'
import { Search } from 'lucide-react'
import { forwardRef, memo } from 'react'
import { cn } from 'utils/cn'

export const Command = memo(
    forwardRef<React.ElementRef<typeof CommandPrimitive>, React.ComponentPropsWithoutRef<typeof CommandPrimitive>>(
        ({ className, ...props }, ref) => (
            <CommandPrimitive
                ref={ref}
                {...props}
                className={cn(
                    'bg-popover text-popover-foreground flex h-full w-full flex-col overflow-hidden rounded-md',
                    className,
                )}
            />
        ),
    ),
)
Command.displayName = CommandPrimitive.displayName

interface CommandDialogProps extends DialogProps {
    filter?: React.ComponentPropsWithoutRef<typeof Command>['filter']
}
export const CommandDialog: React.FC<CommandDialogProps> = memo(({ children, filter, ...props }) => {
    return (
        <Dialog {...props}>
            <DialogContent className="overflow-hidden p-0 shadow-lg">
                <Command
                    filter={filter}
                    className="[&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5"
                >
                    {children}
                </Command>
            </DialogContent>
        </Dialog>
    )
})

export const CommandInput = memo(
    forwardRef<React.ElementRef<typeof CommandPrimitive.Input>, React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>>(
        ({ className, ...props }, ref) => (
            <div className="flex items-center border-b px-3" cmdk-input-wrapper="">
                <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                <CommandPrimitive.Input
                    ref={ref}
                    {...props}
                    className={cn(
                        'placeholder:text-muted-foreground flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50',
                        className,
                    )}
                />
            </div>
        ),
    ),
)
CommandInput.displayName = CommandPrimitive.Input.displayName

export const CommandList = memo(
    forwardRef<React.ElementRef<typeof CommandPrimitive.List>, React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>>(
        ({ className, ...props }, ref) => (
            <CommandPrimitive.List
                ref={ref}
                {...props}
                className={cn('max-h-[300px] overflow-y-auto overflow-x-hidden', className)}
            />
        ),
    ),
)
CommandList.displayName = CommandPrimitive.List.displayName

export const CommandEmpty = memo(
    forwardRef<React.ElementRef<typeof CommandPrimitive.Empty>, React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>>(
        (props, ref) => {
            return <CommandPrimitive.Empty ref={ref} {...props} className="py-6 text-center text-sm" />
        },
    ),
)
CommandEmpty.displayName = CommandPrimitive.Empty.displayName

export const CommandGroup = memo(
    forwardRef<React.ElementRef<typeof CommandPrimitive.Group>, React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>>(
        ({ className, ...props }, ref) => (
            <CommandPrimitive.Group
                ref={ref}
                {...props}
                className={cn(
                    'text-foreground [&_[cmdk-group-heading]]:text-muted-foreground overflow-hidden p-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium',
                    className,
                )}
            />
        ),
    ),
)
CommandGroup.displayName = CommandPrimitive.Group.displayName

export const CommandSeparator = memo(
    forwardRef<
        React.ElementRef<typeof CommandPrimitive.Separator>,
        React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
    >(({ className, ...props }, ref) => (
        <CommandPrimitive.Separator ref={ref} {...props} className={cn('bg-border -mx-1 h-px', className)} />
    )),
)
CommandSeparator.displayName = CommandPrimitive.Separator.displayName

export const CommandItem = memo(
    forwardRef<React.ElementRef<typeof CommandPrimitive.Item>, React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>>(
        ({ className, ...props }, ref) => (
            <CommandPrimitive.Item
                ref={ref}
                {...props}
                className={cn(
                    'aria-selected:bg-accent aria-selected:text-accent-foreground relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                    className,
                )}
            />
        ),
    ),
)
CommandItem.displayName = CommandPrimitive.Item.displayName

export const CommandShortcut = memo(
    forwardRef<React.ComponentRef<'span'>, React.ComponentProps<'span'>>(({ className, ...props }, ref) => {
        return <span ref={ref} {...props} className={cn('text-muted-foreground ml-auto text-xs tracking-widest', className)} />
    }),
)
CommandShortcut.displayName = CommandShortcut.name
